import type { FormEvent } from 'react';
import { memo, useEffect, useRef, useState } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { DEV_AUTH_CODE } from '../../config';
import buildClassName from '../../util/buildClassName';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import TelegrafLogo from '../cx/common/TelegrafLogo';

import styles from './AuthEmail.module.scss';

type StateProps = Pick<GlobalState['auth'], 'isLoading' | 'errorKey' | 'email'>;

const CODE_LENGTH = 5;

const AuthEmailCode = ({ isLoading, errorKey, email }: StateProps) => {
  const { setAuthCode, clearAuthErrorKey, returnToAuthPhoneNumber } = getActions();

  const inputRef = useRef<HTMLInputElement>();
  const [code, setCode] = useState('');

  const lang = useLang();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (errorKey) {
      setCode('');
      inputRef.current?.focus();
    }
  }, [errorKey]);

  const handleInput = useLastCallback((e: FormEvent<HTMLInputElement>) => {
    if (errorKey) clearAuthErrorKey();

    const value = e.currentTarget.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(value);

    if (value.length === CODE_LENGTH && !isLoading) {
      setAuthCode({ code: value });
    }
  });

  const handleBack = useLastCallback(() => {
    returnToAuthPhoneNumber();
  });

  const cells = Array.from({ length: CODE_LENGTH }, (_, i) => code[i] || '');

  return (
    <div className={styles.root}>
      <TelegrafLogo className={styles.logo} withWordmark />
      <h1 className={styles.title}>{lang('CxAuthCodeTitle')}</h1>
      <p className={styles.subtitle}>
        {lang('CxAuthCodeSubtitle', { email: email || '', count: CODE_LENGTH }, {
          withNodes: true, withMarkdown: true,
        })}
      </p>
      <div className={styles.codeInputWrapper}>
        {cells.map((digit, i) => (
          <div key={i} className={buildClassName(styles.codeCell, i === code.length && styles.codeCellActive)}>
            {digit}
          </div>
        ))}
        <input
          ref={inputRef}
          className={styles.codeHiddenInput}
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          disabled={isLoading}
          onInput={handleInput}
        />
      </div>
      {errorKey && <p className={styles.codeError}>{lang.withRegular(errorKey)}</p>}
      {DEV_AUTH_CODE && (
        <p className={styles.devHint}>
          {lang('CxAuthDevCodeHint', { code: DEV_AUTH_CODE }, { withNodes: true, withMarkdown: true })}
        </p>
      )}
      <p className={styles.hint}>
        {lang('CxAuthWrongEmail')}
        {' '}
        <span className={styles.link} onClick={handleBack}>{lang('CxAuthChangeEmail')}</span>
      </p>
    </div>
  );
};

export default memo(withGlobal((global): Complete<StateProps> => {
  const { isLoading, errorKey, email } = global.auth;

  return {
    isLoading,
    errorKey,
    email,
  };
})(AuthEmailCode));
